import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  RefreshCw, 
  TrendingUp, 
  TrendingDown, 
  Package, 
  Activity,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Layers,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const TransactionDashboard = () => {
  const navigate = useNavigate();
  
  // State
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Date range state - default to today
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  
  // Auto-refresh interval (30 seconds)
  const REFRESH_INTERVAL = 30000;

  const fetchDashboardData = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const response = await apiClient.get('/transaction-dashboard/summary', {
        params: { start_date: startDate, end_date: endDate }
      });
      
      setDashboardData(response.data);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [startDate, endDate]);

  // Initial load and date change
  useEffect(() => {
    fetchDashboardData(false);
  }, [fetchDashboardData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDashboardData(true);
    }, REFRESH_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [fetchDashboardData]);

  const toggleSectionExpand = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  const handleDateChange = () => {
    fetchDashboardData(false);
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  // Get section status color
  const getSectionStatusColor = (section) => {
    if (!section.has_data) return 'bg-slate-100 dark:bg-slate-800';
    if (section.net_balance > 0) return 'bg-emerald-50 dark:bg-emerald-950/30';
    if (section.net_balance < 0) return 'bg-amber-50 dark:bg-amber-950/30';
    return 'bg-blue-50 dark:bg-blue-950/30';
  };

  if (isLoading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Loading transaction data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/admin/dashboard')}
                className="shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                  Transaction Dashboard
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Real-time inward/outward across all sections
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Date Range Picker */}
              <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-lg px-3 py-2 border border-slate-200 dark:border-slate-700">
                <Calendar className="h-4 w-4 text-slate-500" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-[130px] h-7 border-0 p-0 focus:ring-0 text-sm"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-[130px] h-7 border-0 p-0 focus:ring-0 text-sm"
                />
              </div>
              
              {/* Refresh Button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleManualRefresh}
                disabled={isRefreshing}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
          
          {/* Last Updated indicator */}
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500 dark:text-slate-400">
            <Clock className="h-3 w-3" />
            <span>Last updated: {formatTimeAgo(lastUpdated)}</span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <span>Auto-refreshes every 30s</span>
            {isRefreshing && (
              <Badge variant="secondary" className="text-xs py-0 px-1.5">
                <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                Refreshing
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Summary Cards */}
        {dashboardData?.summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Sections</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                      {dashboardData.summary.total_sections}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Layers className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  {dashboardData.summary.active_sections} active today
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Inward</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                      {dashboardData.summary.total_inward.toLocaleString()}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Outward</p>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                      {dashboardData.summary.total_outward.toLocaleString()}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <TrendingDown className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">Net Balance</p>
                    <p className={`text-2xl font-bold mt-1 ${
                      dashboardData.summary.total_net_balance >= 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {dashboardData.summary.total_net_balance >= 0 ? '+' : ''}{dashboardData.summary.total_net_balance.toLocaleString()}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Activity className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Section Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dashboardData?.sections?.map((section) => (
            <Card 
              key={section.section_name}
              className={`transition-all duration-200 hover:shadow-md border-slate-200 dark:border-slate-700 ${getSectionStatusColor(section)}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Package className="h-4 w-4 text-slate-500" />
                    {section.section_name}
                  </CardTitle>
                  {section.has_data ? (
                    <Badge 
                      variant="secondary" 
                      className={`text-xs ${
                        section.net_balance > 0 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' 
                          : section.net_balance < 0 
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                      }`}
                    >
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-slate-500">
                      No Data
                    </Badge>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                {/* Main Stats */}
                <div className="grid grid-cols-3 gap-2 py-3">
                  <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Inward</p>
                    <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      {section.inward}
                    </p>
                  </div>
                  <div className="text-center border-x border-slate-200 dark:border-slate-700">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Outward</p>
                    <p className="text-lg font-bold text-amber-600 dark:text-amber-400">
                      {section.outward}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400">Balance</p>
                    <p className={`text-lg font-bold ${
                      section.net_balance >= 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {section.net_balance >= 0 ? '+' : ''}{section.net_balance}
                    </p>
                  </div>
                </div>
                
                {/* Operation Breakdown (expandable) */}
                {section.has_data && Object.keys(section.operations || {}).length > 0 && (
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
                    <button
                      onClick={() => toggleSectionExpand(section.section_name)}
                      className="w-full flex items-center justify-between text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 py-1"
                    >
                      <span>Operation Breakdown</span>
                      {expandedSections[section.section_name] ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    
                    {expandedSections[section.section_name] && (
                      <div className="mt-2 space-y-1.5 animate-in slide-in-from-top-2 duration-200">
                        {Object.entries(section.operations).map(([opName, counts]) => (
                          <div 
                            key={opName} 
                            className="flex items-center justify-between text-xs bg-white/50 dark:bg-slate-800/50 rounded px-2 py-1.5"
                          >
                            <span className="text-slate-700 dark:text-slate-300 truncate flex-1 mr-2">
                              {opName}
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                                ↑{counts.inward || 0}
                              </span>
                              <span className="text-amber-600 dark:text-amber-400 font-medium">
                                ↓{counts.outward || 0}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* USN counts if available */}
                {(section.inward_usns > 0 || section.outward_usns > 0) && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    USNs: <span className="text-emerald-600">↑{section.inward_usns}</span> / <span className="text-amber-600">↓{section.outward_usns}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {dashboardData?.sections?.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No Section Data
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                No transaction data found for the selected date range.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer Info */}
        <div className="mt-8 text-center text-xs text-slate-500 dark:text-slate-400">
          <p>
            Showing data from <strong>{dashboardData?.start_date}</strong> to <strong>{dashboardData?.end_date}</strong>
            {' '}• Timezone: {dashboardData?.timezone || 'IST (UTC+5:30)'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransactionDashboard;
